import React, { useState, useEffect } from "react";
import "./StatisticsPage.css";

// 더미 데이터 생성 함수
const generateDummyData = () => {
  // 연령대별 환자 분포
  const ageGroups = [
    "0-9",
    "10-19",
    "20-29",
    "30-39",
    "40-49",
    "50-59",
    "60-69",
    "70+",
  ];

  const ageDistribution = ageGroups.map((group) => ({
    group,
    count: Math.floor(Math.random() * 100) + 10,
  }));

  // 월별 추세 데이터
  const months = [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ];
  const monthlyTrends = months.map((month) => ({
    month,
    patients: Math.floor(Math.random() * 80) + 20,
    completedForms: Math.floor(Math.random() * 60) + 10,
  }));

  // 진단 분포
  const diagnosisData = [
    { name: "고혈압", value: Math.floor(Math.random() * 200) + 100 },
    { name: "당뇨", value: Math.floor(Math.random() * 150) + 50 },
    { name: "심혈관 질환", value: Math.floor(Math.random() * 100) + 30 },
    { name: "암", value: Math.floor(Math.random() * 80) + 20 },
    { name: "호흡기 질환", value: Math.floor(Math.random() * 120) + 40 },
    { name: "기타", value: Math.floor(Math.random() * 90) + 60 },
  ];

  // 산점도 데이터
  const scatterData = Array.from({ length: 50 }, () => ({
    age: Math.floor(Math.random() * 80) + 15,
    weight: Math.floor(Math.random() * 60) + 40,
    gender: Math.random() > 0.5 ? "남성" : "여성",
  }));

  return {
    ageDistribution,
    monthlyTrends,
    diagnosisData,
    scatterData,
    totalPatients: Math.floor(Math.random() * 1000) + 500,
    averageAge: Math.floor(Math.random() * 20) + 40,
    admissionRate: (Math.random() * 0.3 + 0.1).toFixed(2),
    completedCases: Math.floor(Math.random() * 400) + 200,
  };
};

// 막대 차트 컴포넌트
const BarChart = ({ data, title }) => {
  const [showPercentage, setShowPercentage] = useState(false);
  const maxValue = Math.max(...data.map((item) => item.value));

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
        <div className="chart-options">
          <div
            className={`chart-option ${!showPercentage ? "active" : ""}`}
            onClick={() => setShowPercentage(false)}
          >
            값
          </div>
          <div
            className={`chart-option ${showPercentage ? "active" : ""}`}
            onClick={() => setShowPercentage(true)}
          >
            백분율
          </div>
        </div>
      </div>
      <div className="bar-chart">
        <div className="chart-bars">
          {data.map((item, index) => {
            const height = (item.value / maxValue) * 200;
            const displayValue = showPercentage
              ? `${Math.round((item.value / maxValue) * 100)}%`
              : item.value;

            return (
              <div
                key={index}
                className="chart-bar"
                style={{ height: `${height}px` }}
              >
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
        <div className="chart-labels">
          {data.map((item, index) => (
            <span key={index}>{item.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// 라인 차트 컴포넌트
const LineChart = ({ data, title }) => {
  const [timeframe, setTimeframe] = useState("monthly");
  const chartWidth = 800;
  const chartHeight = 200;
  const paddingX = 40;
  const paddingY = 30;

  const filteredData =
    timeframe === "monthly"
      ? data.monthly
      : timeframe === "weekly"
      ? data.weekly
      : data.daily;

  const maxValue = Math.max(...filteredData.map((item) => item.value)) * 1.2;
  const dataLength = filteredData.length;

  // 데이터 포인트의 좌표 계산
  const points = filteredData.map((item, index) => {
    const x =
      paddingX + (index / (dataLength - 1)) * (chartWidth - paddingX * 2);
    const y =
      chartHeight -
      paddingY -
      (item.value / maxValue) * (chartHeight - paddingY * 2);
    return { x, y, label: item.label, value: item.value };
  });

  // 포인트를 연결하는 경로
  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x},${point.y}`)
    .join(" ");

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
        <div className="chart-options">
          <div
            className={`chart-option ${timeframe === "daily" ? "active" : ""}`}
            onClick={() => setTimeframe("daily")}
          >
            일별
          </div>
          <div
            className={`chart-option ${timeframe === "weekly" ? "active" : ""}`}
            onClick={() => setTimeframe("weekly")}
          >
            주별
          </div>
          <div
            className={`chart-option ${
              timeframe === "monthly" ? "active" : ""
            }`}
            onClick={() => setTimeframe("monthly")}
          >
            월별
          </div>
        </div>
      </div>
      <div className="line-chart">
        <svg className="line-svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
          {/* 격자 */}
          {[...Array(5)].map((_, i) => {
            const y = paddingY + i * ((chartHeight - paddingY * 2) / 4);
            return (
              <line
                key={`grid-h-${i}`}
                x1={paddingX}
                y1={y}
                x2={chartWidth - paddingX}
                y2={y}
                stroke="#f1f5f9"
                strokeWidth="1"
              />
            );
          })}

          {/* Y축 */}
          <line
            x1={paddingX}
            y1={paddingY}
            x2={paddingX}
            y2={chartHeight - paddingY}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          {/* X축 */}
          <line
            x1={paddingX}
            y1={chartHeight - paddingY}
            x2={chartWidth - paddingX}
            y2={chartHeight - paddingY}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          {/* 라인 패스 */}
          <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth="2" />

          {/* 데이터 포인트 */}
          {points.map((point, index) => (
            <g key={`point-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill="#3b82f6"
                stroke="white"
                strokeWidth="2"
              />

              {/* X 축 레이블 */}
              {index % Math.ceil(dataLength / 6) === 0 && (
                <text
                  x={point.x}
                  y={chartHeight - 10}
                  fontSize="10"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {point.label}
                </text>
              )}

              {/* 툴팁 - 실제 애플리케이션에서는 마우스오버시 표시 */}
              <title>{`${point.label}: ${point.value}`}</title>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

// 파이 차트 컴포넌트
const PieChart = ({ data, title }) => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
    "#6366f1",
    "#14b8a6",
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // 각 세그먼트의 각도 계산
  let startAngle = 0;
  const segments = data.map((item, index) => {
    const percentage = item.value / total;
    const angle = percentage * 360;
    const endAngle = startAngle + angle;

    // SVG 아크 경로 계산
    const x1 = 90 + 80 * Math.cos(((startAngle - 90) * Math.PI) / 180);
    const y1 = 90 + 80 * Math.sin(((startAngle - 90) * Math.PI) / 180);
    const x2 = 90 + 80 * Math.cos(((endAngle - 90) * Math.PI) / 180);
    const y2 = 90 + 80 * Math.sin(((endAngle - 90) * Math.PI) / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathData = `M 90 90 L ${x1} ${y1} A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    const segment = {
      path: pathData,
      color: colors[index % colors.length],
      percentage,
      label: item.label,
      value: item.value,
    };

    startAngle = endAngle;
    return segment;
  });

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
      </div>
      <div className="pie-chart">
        <div className="pie-segments">
          <svg width="180" height="180" viewBox="0 0 180 180">
            {segments.map((segment, index) => (
              <path
                key={index}
                d={segment.path}
                fill={segment.color}
                stroke="white"
                strokeWidth="1"
              >
                <title>{`${segment.label}: ${segment.value} (${Math.round(
                  segment.percentage * 100
                )}%)`}</title>
              </path>
            ))}
          </svg>
        </div>
        <div className="pie-legend">
          {segments.map((segment, index) => (
            <div key={index} className="legend-item">
              <div
                className="color-dot"
                style={{ backgroundColor: segment.color }}
              ></div>
              <span>{segment.label}</span>
              <span style={{ marginLeft: "auto" }}>
                {segment.value} ({Math.round(segment.percentage * 100)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 산점도 컴포넌트
const ScatterPlot = ({ data, title }) => {
  const chartWidth = 800;
  const chartHeight = 240;
  const paddingX = 60;
  const paddingY = 30;

  // X와 Y 값의 최대/최소 찾기
  const maxX = Math.max(...data.map((item) => item.x)) * 1.1;
  const maxY = Math.max(...data.map((item) => item.y)) * 1.1;
  const minX = Math.min(...data.map((item) => item.x)) * 0.9;
  const minY = Math.min(...data.map((item) => item.y)) * 0.9;

  // 데이터 포인트의 좌표 계산
  const points = data.map((item) => {
    const x =
      paddingX +
      ((item.x - minX) / (maxX - minX)) * (chartWidth - paddingX * 2);
    const y =
      chartHeight -
      paddingY -
      ((item.y - minY) / (maxY - minY)) * (chartHeight - paddingY * 2);
    return { x, y, ...item };
  });

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
      </div>
      <div className="scatter-plot">
        <svg
          className="scatter-svg"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        >
          {/* 격자 */}
          {[...Array(5)].map((_, i) => {
            const y = paddingY + i * ((chartHeight - paddingY * 2) / 4);
            return (
              <line
                key={`grid-h-${i}`}
                x1={paddingX}
                y1={y}
                x2={chartWidth - paddingX}
                y2={y}
                stroke="#f1f5f9"
                strokeWidth="1"
              />
            );
          })}

          {[...Array(6)].map((_, i) => {
            const x = paddingX + i * ((chartWidth - paddingX * 2) / 5);
            return (
              <line
                key={`grid-v-${i}`}
                x1={x}
                y1={paddingY}
                x2={x}
                y2={chartHeight - paddingY}
                stroke="#f1f5f9"
                strokeWidth="1"
              />
            );
          })}

          {/* Y축 */}
          <line
            x1={paddingX}
            y1={paddingY}
            x2={paddingX}
            y2={chartHeight - paddingY}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          {/* X축 */}
          <line
            x1={paddingX}
            y1={chartHeight - paddingY}
            x2={chartWidth - paddingX}
            y2={chartHeight - paddingY}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          {/* X축 레이블 */}
          <text
            x={chartWidth / 2}
            y={chartHeight - 5}
            fontSize="10"
            fill="#64748b"
            textAnchor="middle"
          >
            환자 나이
          </text>

          {/* Y축 레이블 */}
          <text
            x={15}
            y={chartHeight / 2}
            fontSize="10"
            fill="#64748b"
            textAnchor="middle"
            transform={`rotate(-90, 15, ${chartHeight / 2})`}
          >
            증상 기간(일)
          </text>

          {/* 데이터 포인트 */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="5"
              fill={point.gender === "M" ? "#3b82f6" : "#ec4899"}
              opacity="0.7"
              stroke="white"
              strokeWidth="1"
            >
              <title>{`환자: ${point.id}, 나이: ${point.x}, 증상 기간: ${
                point.y
              }일, 성별: ${point.gender === "M" ? "남성" : "여성"}`}</title>
            </circle>
          ))}

          {/* 범례 */}
          <circle cx={chartWidth - 100} cy={20} r="4" fill="#3b82f6" />
          <text x={chartWidth - 90} y={23} fontSize="10" fill="#64748b">
            남성
          </text>

          <circle cx={chartWidth - 60} cy={20} r="4" fill="#ec4899" />
          <text x={chartWidth - 50} y={23} fontSize="10" fill="#64748b">
            여성
          </text>
        </svg>
      </div>
    </div>
  );
};

// 메인 통계 페이지 컴포넌트
const StatisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    // 통계 데이터 가져오기 (여기서는 예제 데이터를 사용)
    const fetchStatistics = async () => {
      // 실제 앱에서는 API 호출
      // const response = await fetch('/api/statistics');
      // const data = await response.json();

      // 예제 데이터
      const data = {
        totalPatients: 1248,
        completedForms: 896,
        pendingForms: 352,
        queryRate: 12.5,
        ageDistribution: [
          { label: "10대", value: 78 },
          { label: "20대", value: 242 },
          { label: "30대", value: 384 },
          { label: "40대", value: 305 },
          { label: "50대", value: 189 },
          { label: "60대+", value: 50 },
        ],
        diagnosisDistribution: [
          { label: "고혈압", value: 245 },
          { label: "당뇨", value: 189 },
          { label: "심장질환", value: 127 },
          { label: "호흡기질환", value: 152 },
          { label: "관절염", value: 98 },
          { label: "기타", value: 437 },
        ],
        monthlyTrends: {
          monthly: [
            { label: "1월", value: 78 },
            { label: "2월", value: 94 },
            { label: "3월", value: 112 },
            { label: "4월", value: 135 },
            { label: "5월", value: 127 },
            { label: "6월", value: 142 },
            { label: "7월", value: 158 },
            { label: "8월", value: 137 },
            { label: "9월", value: 143 },
            { label: "10월", value: 156 },
            { label: "11월", value: 148 },
            { label: "12월", value: 132 },
          ],
          weekly: [
            { label: "1주", value: 32 },
            { label: "2주", value: 38 },
            { label: "3주", value: 41 },
            { label: "4주", value: 37 },
            { label: "5주", value: 34 },
            { label: "6주", value: 39 },
            { label: "7주", value: 42 },
            { label: "8주", value: 38 },
          ],
          daily: [
            { label: "월", value: 18 },
            { label: "화", value: 22 },
            { label: "수", value: 28 },
            { label: "목", value: 25 },
            { label: "금", value: 19 },
            { label: "토", value: 8 },
            { label: "일", value: 5 },
          ],
        },
        patientScatter: [
          { id: "P001", x: 45, y: 12, gender: "M" },
          { id: "P002", x: 32, y: 5, gender: "F" },
          { id: "P003", x: 58, y: 30, gender: "M" },
          { id: "P004", x: 22, y: 3, gender: "F" },
          { id: "P005", x: 39, y: 15, gender: "M" },
          { id: "P006", x: 47, y: 8, gender: "F" },
          { id: "P007", x: 61, y: 21, gender: "M" },
          { id: "P008", x: 28, y: 7, gender: "F" },
          { id: "P009", x: 35, y: 14, gender: "M" },
          { id: "P010", x: 42, y: 18, gender: "F" },
          { id: "P011", x: 53, y: 25, gender: "M" },
          { id: "P012", x: 19, y: 2, gender: "F" },
          { id: "P013", x: 67, y: 35, gender: "M" },
          { id: "P014", x: 51, y: 22, gender: "F" },
          { id: "P015", x: 29, y: 9, gender: "M" },
          { id: "P016", x: 44, y: 19, gender: "F" },
          { id: "P017", x: 38, y: 11, gender: "M" },
          { id: "P018", x: 26, y: 6, gender: "F" },
          { id: "P019", x: 55, y: 28, gender: "M" },
          { id: "P020", x: 33, y: 10, gender: "F" },
        ],
      };

      setStatistics(data);
      setLoading(true);
      // 로딩 효과를 위한 지연
      setTimeout(() => {
        setLoading(false);
      }, 800);
    };

    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <div className="statistics-container loading">데이터 로딩 중...</div>
    );
  }

  return (
    <div className="statistics-container">
      <div className="statistics-header">
        <h2>eCRF 통계 데이터</h2>
        <p>프로젝트에 대한 통계 및 분석 정보</p>
      </div>

      <div className="statistics-filters">
        <div className="filter-group">
          <label>기간 선택</label>
          <select>
            <option value="all">전체 기간</option>
            <option value="1m">최근 1개월</option>
            <option value="3m">최근 3개월</option>
            <option value="6m">최근 6개월</option>
            <option value="1y">최근 1년</option>
          </select>
        </div>

        <div className="filter-group">
          <label>연령대</label>
          <select>
            <option value="all">전체 연령대</option>
            <option value="10s">10대</option>
            <option value="20s">20대</option>
            <option value="30s">30대</option>
            <option value="40s">40대</option>
            <option value="50s">50대</option>
            <option value="60s">60대 이상</option>
          </select>
        </div>

        <div className="filter-group">
          <label>진단명</label>
          <select>
            <option value="all">전체 진단명</option>
            <option value="hypertension">고혈압</option>
            <option value="diabetes">당뇨</option>
            <option value="heart">심장질환</option>
            <option value="respiratory">호흡기질환</option>
            <option value="arthritis">관절염</option>
            <option value="other">기타</option>
          </select>
        </div>

        <button className="export-btn">데이터 내보내기</button>
      </div>

      <div className="statistics-summary">
        <div className="stat-card">
          <h3>총 환자 수</h3>
          <div className="stat-value">{statistics.totalPatients}</div>
          <div className="stat-trend positive">
            <span>+5.2%</span> 지난 달 대비
          </div>
        </div>

        <div className="stat-card">
          <h3>완료된 양식</h3>
          <div className="stat-value">{statistics.completedForms}</div>
          <div className="stat-trend positive">
            <span>+8.3%</span> 지난 달 대비
          </div>
        </div>

        <div className="stat-card">
          <h3>대기 중인 양식</h3>
          <div className="stat-value">{statistics.pendingForms}</div>
          <div className="stat-trend negative">
            <span>-2.1%</span> 지난 달 대비
          </div>
        </div>

        <div className="stat-card">
          <h3>쿼리 비율</h3>
          <div className="stat-value">{statistics.queryRate}%</div>
          <div className="stat-trend neutral">변동 없음</div>
        </div>
      </div>

      <div className="statistics-grid">
        <BarChart
          data={statistics.ageDistribution}
          title="연령대별 환자 분포"
        />

        <PieChart
          data={statistics.diagnosisDistribution}
          title="진단명별 환자 분포"
        />

        <LineChart
          data={statistics.monthlyTrends}
          title="월별 환자 등록 추이"
        />

        <ScatterPlot
          data={statistics.patientScatter}
          title="환자 나이 vs 증상 기간"
        />
      </div>
    </div>
  );
};

export default StatisticsPage;
